import { formatDate } from '@amelio/ui-library';

import type { ReviewInstance } from '@/types/review/ReviewInstance';

import { ReviewType } from '@/constants/review/CycleInstance';
import { isAnonymousFeedback } from '@/constants/review/FeedbackPrivacy';
import { ReviewInstanceStatus } from '@/constants/review/ReviewInstanceStatus';

interface ResponseCounts {
    count: number;
    minimum: number;
}

export type BelowThresholdState =
    | ({ kind: 'waiting' } & ResponseCounts)
    | ({ kind: 'closed'; closedOn?: string } & ResponseCounts);

/** What the results header says of a feedback that stopped collecting, and the day it did. */
export interface ResultsStatus {
    kind: 'closed' | 'completed';
    date?: string;
}

/** The day an anonymous feedback expired, above its minimum or below it: its due date, which every reader knows. */
export const getAnonymousClosedOn = (
    reviewInstance: ReviewInstance,
    language: string,
): string | undefined => {
    const dueDate = reviewInstance.cycleInstance?.dueDate;

    return isAnonymousFeedback(reviewInstance.feedbackPrivacy) &&
        reviewInstance.status === ReviewInstanceStatus.EXPIRED &&
        dueDate
        ? formatDate({ date: dueDate, language })
        : undefined;
};

/**
 * Where an anonymous feedback stands while its answers are withheld: still collecting, or closed short of its
 * minimum. Null when its results show, or when it names its respondents and so withholds nothing.
 */
export const getBelowThresholdState = (
    reviewInstance: ReviewInstance,
    language: string,
): BelowThresholdState | null => {
    if (!isAnonymousFeedback(reviewInstance.feedbackPrivacy) || reviewInstance.resultsAvailable) {
        return null;
    }

    const counts = {
        count: reviewInstance.completedResponseCount,
        minimum: reviewInstance.minimumResponses,
    };

    if (reviewInstance.status === ReviewInstanceStatus.ACTIVE) {
        return { kind: 'waiting', ...counts };
    }

    return { kind: 'closed', closedOn: getAnonymousClosedOn(reviewInstance, language), ...counts };
};

/**
 * A 360 that got all its responses says so. An anonymous one says on what day, which the server gives alone, its hour
 * being that of its last respondent; a named one leaves its date to the header. Withheld answers say the feedback
 * closed, and their header says the same.
 */
export const getResultsStatus = (
    reviewInstance: ReviewInstance,
    belowThreshold: BelowThresholdState | null,
    language: string,
): ResultsStatus | null => {
    if (belowThreshold) {
        return belowThreshold.kind === 'closed'
            ? { kind: 'closed', date: belowThreshold.closedOn }
            : null;
    }

    const closedOn = getAnonymousClosedOn(reviewInstance, language);
    if (closedOn) {
        return { kind: 'closed', date: closedOn };
    }

    if (
        reviewInstance.status !== ReviewInstanceStatus.COMPLETED ||
        reviewInstance.cycleInstance?.reviewType !== ReviewType.EVALUATION_360
    ) {
        return null;
    }

    const { completedOn } = reviewInstance;

    return {
        kind: 'completed',
        date: completedOn ? formatDate({ date: completedOn, language }) : undefined,
    };
};
